/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import {
  Autocomplete,
  FormControl,
  Grid,
} from '@mui/material';
import { useLang } from '~/hooks/useLang';
import useDispatchRegistrationPersonState from '../../../../stores/DispatchRegistrationPersonState';
import useDispatchRegistrationState from '../../../../stores/DispatchRegistrationState';
import { maskCEP, removeMask, maskAddress } from '../../../../utils/stringsManipulations';
import { IState } from '~/features/Dispatch/interfaces/IDispacthRegistrationVehicle';
import ICity from '~/features/City/interfaces/ICity';
import FieldAutocompleteRegistration from '~/components/FieldAutocompleteRegistration';
import FieldTextRegistration from '~/components/FieldTextRegistration';
import { useHookstate } from '@hookstate/core';

interface FormPeopleAddressProps {
  readonly disableFields?: boolean
}

interface statesResponse {
  readonly id: number,
  readonly name: string,
  readonly uf: string,
  readonly created_at: string | null,
  readonly updated_at: string | null,
  readonly deleted_at: string | null
}

interface citiesResponse {
  readonly id: number,
  readonly name: string,
  readonly state_id: number,
  readonly created_at: string | null
  readonly updated_at: string | null
  readonly deleted_at: string | null
 }


export default function FormPeopleAddress({ disableFields = false }: FormPeopleAddressProps) {
  const { translate } = useLang();
  const { getStates, getCities, getAddress } = useDispatchRegistrationState();
  const { registrationPersonDispatch } = useDispatchRegistrationPersonState();
  const { person } = registrationPersonDispatch();
  const statesList = useHookstate<IState[]>([]);
  const citiesList = useHookstate<ICity[]>([]);

  const populateStateFromCEP = async (addressViaCep) => {
    const states: statesResponse[] = await getStates();

    states.map((state) => {
      statesList.merge([{
        id: String(state.id),
        name: state.name,
        uf: state.uf
      }])
    })

    return statesList.get().find(state => state.uf === addressViaCep.data.uf)
  }

  const populateCityFromCEP = async (addressViaCep, state) => {
    const cities: citiesResponse[] = await getCities(state?.id);
    cities.map((city) => {
      citiesList.merge([{
        id: city.id,
        name: city.name,
        stateId: city.state_id,
        state: {
          name: String(state?.name),
        }
      }])
    })

    return citiesList.get().find(city => city.id === parseInt(addressViaCep.data.ibge))
  }

  const updateAddress = async ({ cep }) => {
    try {
      const addressViaCep = await getAddress(removeMask(cep));

      if (!addressViaCep.data.erro) {

        const state = await populateStateFromCEP(addressViaCep);
        const city = await populateCityFromCEP(addressViaCep, state);

        person.merge({
          district: addressViaCep.data.bairro,
          address: addressViaCep.data.logradouro,
          stateId: Number(state?.id),
          cityId: Number(city?.id),
        })
      }
    } catch (error) {
      console.error("Erro ao buscar endereço:", error);
    }
  }

  



  return (
    <FormControl fullWidth size='small' >
      <Grid container direction={'row'}>
        <Grid item xs={2.5} sx={{ padding: 1 }}>
          <TextField
            disabled={disableFields}
            fullWidth
            size='small'
            id="outlined-cep"
            label={translate('CEP')}
            value={person.get().cep}
            onChange={async (event) => {
              const cep = event.target.value;
              person.merge({
                cep: maskCEP(cep)
              })

              if (cep.length === 9) {
                await updateAddress({ cep })
              } else {
                person.merge({
                  district: '',
                  address: '',
                  cityId: '',
                  stateId: ''
                })
              }
            }}
          />
        </Grid>
        <Grid item xs={5} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            value={person.address.get()}
            onChangeValue={(value) => person.address.set(value)}
            label={'Address'}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            value={person.district.get()}
            onChangeValue={(value) => person.district.set(value)}
            label={'District'}
          />
        </Grid>
        <Grid item xs={1.5} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            value={person.addressNumber.get()}
            onChangeValue={(value) => person.addressNumber.set(value)}
            label={'Number'}
            type={'number'}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            value={person.addressReference.get()}
            onChangeValue={(value) => person.addressReference.set(value)}
            label={'Address Reference'}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('State')}
            valueId={String(person.stateId.get())}
            onValueChange={(state) => {
              person.merge({
                stateId: state?.id,
                cityId: null
              })
            }}
            optionsFetch={getStates}
            disabled={disableFields}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('City')}
            onValueChange={(city) => {
              if (!city) {
                return
              }
              person.merge({
                cityId: city?.id,
              })
            }}
            valueId={String(person.cityId.get())}
            optionsFetch={getCities}
            disabled={!person.stateId.get() || person.stateId.get() === '0' || disableFields}
            depends={person.stateId.get() as string}
          />
        </Grid>
      </Grid>
    </FormControl >
  );
}